import React, { FunctionComponent, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  IonButtons,
  IonContent,
  IonHeader,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { cloudDownload } from "ionicons/icons";
import { ZoneList } from "../../modules/site/components/zone-list.component";
import { SiteCard } from "../../modules/site/components/site-card.component";
import { SitePageFooterComponent } from "../../modules/site/components/site-page-footer.component";
import { DownloadPopover } from "../../modules/site/components/download-popover.component";
import { DownloadStateEnum } from "../../modules/site/models/download-state.enum";
import { AllParams, ParamsNames } from "../../modules/router/constantes/paramsNames";
import { EditIonBackButton } from "../../modules/shared/components/edit-ion-back-button.component";
import { useEditRefresher } from "../../modules/shared/hooks/use-edit-refresher.hook";
import { useSite } from "../../modules/site/hooks/use-site.hook";
import { useSites } from "../../modules/site/hooks/use-sites.hook";
import { LoadingSiteListComponent } from '../../modules/sites/components/loading-site-list.component';
import './site.page.scss';

export const SitePage: FunctionComponent = () => {
  const { t } = useTranslation();

  const params = useParams<Pick<AllParams, ParamsNames.SITE_ID>>();

  const {
    site,
    fetchSite,
    togglePopover,
    isPopoverOpened,
    launchDownload,
    downloadingState,
    launchSync,
    synchronizationState,
  } = useSite(params.siteId);

  const { ionRefresherRef, refreshCallback } = useEditRefresher(fetchSite);
  const isOffline = useSites(site);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const prevSyncStateRef = useRef(synchronizationState);

  // Rafraîchir l'affichage après la synchronisation
  useEffect(() => {
    if (prevSyncStateRef.current && !synchronizationState) {
      // La synchronisation vient de se terminer
      setRefreshTrigger(prev => prev + 1);
    }
    prevSyncStateRef.current = synchronizationState;
  }, [synchronizationState]);

  const displayButton = (site: { isExported: boolean; numberOfExportedBuildings: number }): boolean => {
    if (site.isExported) return false;
    return (site.numberOfExportedBuildings === 0 || site.numberOfExportedBuildings === undefined);
  };

  return (
    <IonPage>
      { site == null ? (<LoadingSiteListComponent message={ t('site.loadingMessage') }/>) :
      <IonHeader role="header">
        <IonToolbar>
          <IonButtons slot="start">
            <EditIonBackButton defaultHref={"/"} />
          </IonButtons>
          {site && <IonTitle>{site.name}</IonTitle>}
        </IonToolbar>
      </IonHeader>
      }
      { site == null ? null :
      <IonContent>
        <IonRefresher ref={ionRefresherRef} slot="fixed" onIonRefresh={refreshCallback}>
          <IonRefresherContent />
        </IonRefresher>
        {site && (
          <>
            <SiteCard site={site} />
            <ZoneList
              siteId={params.siteId}
              siteName={site.name}
              buildingList={site.buildings}
              hasOutdoorAreas={site.hasOutdoorAreas}
              numberOfAnnotations={site.numberOfAnnotations || 0}
              numberOfAnnotationsOnArea={site.numberOfAnnotationsOnArea || 0}
              siteCompositionLabel={t("site.composition.title")}
              buildingTitleLabel={t("site.composition.buildingList.title")}
              outdoorAreasTitleLabel={t("site.composition.outdoorAreas.title")}
              isExported={site.isExported}
              isBuildingList={false}
              isOffline={isOffline}
              refreshTrigger={refreshTrigger}
            />
          </>
        )}
      </IonContent>
      }
      { site == null ? null :
      <DownloadPopover
        titleLabel={t("site.downloadPopover.title")}
        contentLabel={t("site.downloadPopover.content")}
        timingNoticeLabel={t("site.downloadPopover.timingNotice")}
        cancelButtonLabel={t("site.downloadPopover.cancelButton")}
        downloadButtonLabel={t("site.downloadPopover.downloadButton")}
        launchDownload={launchDownload}
        launchSync={launchSync}
        closePopover={() => togglePopover(false)}
        isPopoverOpened={isPopoverOpened}
        downloadInProgress={downloadingState === DownloadStateEnum.IN_PROGRESS}
        buildings={site.buildings}
        siteId={params.siteId}
        hasOutdoorAreas={site.hasOutdoorAreas}
        outdoorAreasTitleLabel={t("site.composition.outdoorAreas.title")}
        syncButtonLabel={t("site.syncButton")}
        synchronizingLabel={t("site.synchronizingMessage")}
        synchronizationState={synchronizationState}
      />
      }

      {site && displayButton(site) ? (
        <SitePageFooterComponent
          onClick={() => togglePopover(true)}
          icon={cloudDownload}
          color={"secondary"}
          footerLabel={t("site.downloadButton")}
        />
      ) : null}
    </IonPage>

  );
};
